/**
 * Trainee Portal — Story 7e.
 *
 * The signed-in trainee's view of their own assignments. Distinct from the
 * coordinator console (Workforce / Orientation Assignment / Review Queue): reads
 * trainee-scoped `my_*` views Samuel added in Gap 2 (2026-06-02).
 *
 * Step progression is driven by Unreal once the orientation is launched
 * (`report_step_beat` server-tracks beat progress to gate the next-step
 * marker). The portal exposes only Launch — manual Advance / Mark complete
 * controls were removed per Cloud Lead 2026-06-22; trainees who need to
 * progress re-enter Unreal via Launch.
 *
 * Empty state for users without an employee binding ("you don't have a
 * MineTwin workforce record yet") — this is the natural gate; admins
 * previewing the portal will see this until they're added as an employee.
 */

import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useMemo, useState } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn, tables as minetwinTables } from '@sdk/minetwin';
import { formatLegacyVersionTag } from '@/lib/orientationVersion';
import { joinedTally, lobbyWaitText, runsLedBy } from '@/lib/groupRunEntity';
import { useMineZeroLaunch } from '@/hooks/useMineZeroLaunch';
import { buildObserverLaunchArgs } from '@/services/launcher/launcherArgs';
import { useMyAssignments, type MyAssignmentRow } from '@/hooks/useMyAssignments';
import { useOrientations } from '@/hooks/useOrientations';
import { useOrientationStepOrder } from '@/hooks/useOrientationStepOrder';
import { useProjects } from '@/hooks/useProjects';
import { useTweaks } from '@/hooks/useTweaks';
import { useLauncherReadiness, type LauncherDownload } from '@/hooks/useLauncherReadiness';
import type { InstallState } from '@/services/launcher/launchService';
import { useLauncherChannel } from '@/hooks/useLauncherChannel';
import { httpLaunch } from '@/services/launcher/launchService';
import { channelLabel, composeLaunchArgs, resolveChannelArgs } from '@/services/launcher/launcherArgs';
import { tables as launcherTables } from '@sdk/launcher';
import { useAuthKit } from '@/contexts/AuthProvider';
import { useOrgSession } from '@/contexts/OrgContext';
import { useSiteAccess } from '@/hooks/useSiteAccess';
import type { Site, Toast } from '@/data/types';
import { GatedButton } from '@/components/GatedButton';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { PortalIcons } from '@/components/portalIcons';

const STDB_URI = import.meta.env.VITE_STDB_CLOUD_URI as string;

type TraineeProps = { toast: (t: Omit<Toast, 'id'>) => void };

export function TraineePortal({ toast }: TraineeProps) {
  const dialogs = useDialogs();
  const minetwinState = useSpacetimeDB('minetwin');
  const { assignments: allAssignments, isReady: assignmentsReady } =
    useMyAssignments();
  const { orientations, isReady: orientationsReady } = useOrientations();
  const { stepsByOrientation, isReady: stepsReady } = useOrientationStepOrder();
  // Per Cloud Lead 2026-06-18: every coordinator/trainee surface
  // (Workforce / Orientation Assignment / Review Queue / My Orientations) is
  // scoped to the topbar's current site. A trainee assigned across
  // multiple sites switches sites in the topbar; assignments for the
  // other site are hidden, not deleted.
  const { sites } = useProjects();
  const { tweaks } = useTweaks();
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const currentProjectId = currentSite?.projectName ?? '';
  const assignments = useMemo(
    () =>
      currentProjectId
        ? allAssignments.filter((a) => a.projectId === currentProjectId)
        : allAssignments,
    [allAssignments, currentProjectId],
  );
  // Phase 3 collapse: employee + user merged into a single User row. The
  // hook used to read my_employee; now my_user is the unified caller record.
  const [myUserRows] = useTable('minetwin', minetwinTables.my_user);
  // Group runs this trainee belongs to (member-gated view): the lobby /
  // running / paused note on the assignment card (group plan Phase 5). The
  // same vocabulary the Unreal lobby and the agent use.
  const [myGroupRuns] = useTable('minetwin', minetwinTables.my_group_run);
  const groupNoteBySession = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of myGroupRuns) {
      const tag = r.status.tag;
      if (tag === 'Completed' || tag === 'Cancelled') continue;
      const leaderName = r.leaderEmail.split('@')[0];
      const note =
        tag === 'Lobby'
          ? `Group "${r.name}" — ${lobbyWaitText(r.mode.tag, leaderName)}. Launch to wait in the lobby.`
          : tag === 'Paused'
            ? `Group "${r.name}" is paused${r.pausedByEmail ? ` by ${r.pausedByEmail.split('@')[0]}` : ''}.`
            : `Group "${r.name}" is running — launch to join at the current stop.`;
      m.set(r.sessionId.toString(), note);
    }
    return m;
  }, [myGroupRuns]);
  // Phase 1 of MINETWIN-ORIENTATION-LAUNCH-PLAN: shared readiness for the
  // per-row Launch button. The channel comes from the shared launcher selection
  // (the topbar picker) or a VITE_MINEZERO_CHANNEL deployment override, so this
  // button launches the same build the trainee picked in the launcher widget.
  const { channel } = useLauncherChannel();
  const launcher = useLauncherReadiness(channel);
  // Backend context for the Unreal app (org / server / WorkOS org) plus the
  // admin per-channel args. Without these the app starts on its built-in
  // defaults and hangs on "Connection timed out" — see launcherArgs.ts.
  const { organizationId, user } = useAuthKit();
  const { org } = useOrgSession();
  // Runs this user LEADS on the current site. A leader takes no assignment
  // (decision 9), so this is their only way into the lobby from this page.
  // Same launch path as Cohorts / Live Sessions "Enter lobby" (#168).
  const ledRuns = useMemo(
    () => runsLedBy(myGroupRuns, user?.email ?? '', currentProjectId || undefined),
    [myGroupRuns, user?.email, currentProjectId],
  );
  const [runDetails] = useTable('minetwin', minetwinTables.my_group_run_details);
  // Member-scoped like my_group_run: the leader sees every participant of
  // their runs, so "x of y joined" matches the Unreal lobby widget.
  const [runParticipants] = useTable('minetwin', minetwinTables.my_group_run_participants);
  const leaderLaunch = useMineZeroLaunch();
  const [busyRunId, setBusyRunId] = useState<bigint | null>(null);
  async function handleEnterAsLeader(run: (typeof ledRuns)[number]) {
    if (run.sessionId === 0n) {
      toast({ title: 'No session yet', sub: 'The shared session is still provisioning.', kind: 'warn' });
      return;
    }
    setBusyRunId(run.groupRunId);
    try {
      const res = await leaderLaunch.launchWithArgs(
        buildObserverLaunchArgs({ ...leaderLaunch.argContext, sessionId: run.sessionId }),
      );
      toast(
        res.ok
          ? { title: `Entering "${run.name}" as leader`, sub: 'The MineZero client is opening in the group session.', kind: 'good' }
          : { title: 'Launch pending', sub: res.message ?? 'The client could not be launched.', kind: 'warn' },
      );
    } finally {
      setBusyRunId(null);
    }
  }
  const [channelConfigs] = useTable('launcher', launcherTables.app_channel_config);

  const [busyAssignmentId, setBusyAssignmentId] = useState<bigint | null>(null);
  // Unassign is site-admin chrome (ORIENTATION-ROLES P2): `delete_assignment`
  // requires site admin server-side, so anyone else would get a rejected call.
  const canUnassign = useSiteAccess(currentProjectId).siteAdmin;

  const connOnline = minetwinState.isActive;
  const isReady = assignmentsReady && orientationsReady && stepsReady;
  const me = myUserRows[0] ?? null;

  const orientationById = useMemo(() => {
    const m = new Map<string, (typeof orientations)[number]>();
    for (const o of orientations) m.set(o.orientationId, o);
    return m;
  }, [orientations]);

  async function runReducer(
    assignmentId: bigint,
    label: string,
    fn: (conn: MinetwinConn) => void,
  ) {
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn) {
      toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
      return;
    }
    setBusyAssignmentId(assignmentId);
    try {
      fn(conn);
    } catch (err) {
      console.error(`[trainee] ${label} failed`, err);
      toast({
        title: `${label} failed`,
        sub: err instanceof Error ? err.message : String(err),
        kind: 'bad',
      });
    } finally {
      setBusyAssignmentId(null);
    }
  }

  /**
   * Remove one of the caller's own assignments (admin only).
   *
   * Hard `delete_assignment`, not the soft `cancel_assignment` the coordinator
   * console uses: on this page a cancelled assignment stays on screen as a
   * "Cancelled" card, which reads like the button did nothing. Deleting
   * cascades the progress/attempts and closes the bound onboarding session,
   * so there is no undo — hence the confirm.
   */
  async function handleUnassign(a: MyAssignmentRow) {
    const name = orientationById.get(a.orientationId)?.displayName ?? a.orientationId;
    if (
      !(await dialogs.confirm({
        title: `Unassign "${name}"?`,
        body: 'Its progress is deleted and cannot be undone.',
        confirmLabel: 'Unassign',
        danger: true,
      }))
    ) {
      return;
    }
    void runReducer(a.assignmentId, 'Unassign', (conn) => {
      conn.reducers.deleteAssignment({ assignmentId: a.assignmentId });
      toast({ title: `Unassigned ${name}`, kind: 'good' });
    });
  }

  // Phase 1: forward the orientation session id to the local launcher's
  // /launch HTTP API as a `-MpSessionId=<n>` command-line arg. The
  // Unreal client (Phase 2 — Samuel) reads the arg and calls
  // EnterSession against that id once its presence is established, so
  // the trainee lands in the dedicated onboarding session (with the
  // agent pre-invited) instead of the default mp session.
  //
  // We require launchEnabled (launcher EXE online + MineZero app row
  // resolved) because the queueLaunch reducer fallback used by
  // LauncherButton drops launch_args silently — sending the trainee
  // into the default session would defeat the entire point of the
  // Launch button. If the launcher is offline, the button is disabled
  // with a tooltip pointing at LauncherButton in the topbar.
  //
  // exePath may be null when the `app` row has no executable_path set; that's
  // fine — the launcher resolves the exe from the installed `version_id`
  // folder. We send '' (matching the topbar LauncherButton) rather than
  // blocking, so a null path doesn't make the button look broken.
  async function handleLaunch(a: MyAssignmentRow) {
    if (!launcher.launchEnabled) {
      toast({
        title: 'Launcher not ready',
        sub: launcher.statusMessage,
        kind: 'warn',
      });
      return;
    }
    if (a.sessionId === 0n) {
      toast({
        title: 'No session yet',
        sub: 'The onboarding session is still being provisioned. Try again in a moment.',
        kind: 'warn',
      });
      return;
    }
    setBusyAssignmentId(a.assignmentId);
    try {
      const result = await httpLaunch({
        appId: launcher.effectiveAppId,
        exePath: launcher.exePath ?? '',
        versionId: launcher.versionId,
        // Session context + admin channel args FIRST, then the orientation
        // session pointer. Decimal-stringify the bigint so the u64
        // round-trips through the command line losslessly (Number(sessionId)
        // would clamp values >= 2^53).
        launchArgs: [
          composeLaunchArgs({
            orgName: org.slug,
            serverUrl: STDB_URI,
            workosOrgId: organizationId,
            projectName: currentProjectId,
            channelArgs: resolveChannelArgs(
              channelConfigs,
              launcher.effectiveAppId,
              channel,
            ),
          }),
          `-MpSessionId=${a.sessionId.toString()}`,
        ]
          .filter(Boolean)
          .join(' '),
      });
      if ('reason' in result) {
        toast({
          title: 'Launch failed',
          sub: result.reason,
          kind: 'bad',
        });
        return;
      }
      const orientationName =
        orientationById.get(a.orientationId)?.displayName ?? a.orientationId;
      if (result.status === 'downloading') {
        // The version wasn't installed; the launcher is fetching it and will
        // launch automatically when done. Progress shows on the card via
        // `launcher.download` (my_download_progress).
        toast({
          title: 'Downloading application',
          sub: 'It will launch automatically when the download finishes.',
          kind: 'good',
        });
      } else {
        toast({
          title: 'Launching application',
          sub: orientationName,
          kind: 'good',
        });
      }
    } finally {
      setBusyAssignmentId(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>My Orientations</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {me ? <>{me.displayName || me.email}</> : 'Signed in'}
            {currentSite ? <> · {currentSite.name}</> : null}
            {connOnline ? null : <span style={{ color: 'var(--bad)' }}> · offline</span>}
          </div>
        </div>
      </div>

      {/* If `my_user` hasn't populated yet but `my_assignments` has rows,
        the trainee can still see + act on their assignments. The two
        views are scoped server-side independently — `my_user` requires
        the post-`client_connected` User-row drain to have run; the
        assignment view keys on JWT email. So an assignment can land
        before the binding completes. Show a thin banner instead of
        hiding everything. */}
      {!me && assignments.length > 0 && (
        <div
          style={{
            background: 'var(--warn-soft, rgba(232,196,92,0.12))',
            border: '1px solid var(--warn, #E8C45C)',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
            color: 'var(--text)',
            marginBottom: 10,
          }}
        >
          Your workforce record is still finalizing. Your assignments are
          shown below; if anything below looks wrong, ping your coordinator.
        </div>
      )}

      {ledRuns.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-head">
            <div>
              <h3>You lead</h3>
              <div className="sub">
                Groups waiting for you. Enter the lobby and press Start in the client.
              </div>
            </div>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: 10 }}>
            {ledRuns.map((r) => {
              const d = runDetails.find((x) => x.groupRunId === r.groupRunId);
              const tag = r.status.tag;
              const { joined, total } = joinedTally(
                runParticipants.filter((m) => m.groupRunId === r.groupRunId),
              );
              return (
                <div
                  key={r.groupRunId.toString()}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5 }}
                >
                  <div style={{ display: 'grid', gap: 2 }}>
                    <div>
                      <b>{d?.orientationName || 'Orientation'}</b>
                      <span style={{ color: 'var(--muted)', marginLeft: 8 }}>
                        {r.name}{d?.cohortName ? ` · ${d.cohortName}` : ''}
                      </span>
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                      {tag === 'Lobby' ? 'Lobby' : tag === 'Paused' ? 'Paused' : 'Running'} ·{' '}
                      {joined} of {total} joined
                    </div>
                  </div>
                  <GatedButton
                    className="btn sm primary"
                    style={{ marginLeft: 'auto' }}
                    busy={busyRunId !== null}
                    reason={leaderLaunch.canLaunch ? null : 'Start the launcher from the topbar rocket first.'}
                    onClick={() => void handleEnterAsLeader(r)}
                  >
                    {tag === 'Lobby' ? 'Enter lobby' : 'Enter as leader'}
                  </GatedButton>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card">
        {!isReady ? (
          <div
            style={{
              padding: 22,
              textAlign: 'center',
              color: 'var(--muted)',
              fontSize: 12.5,
            }}
          >
            Loading your assignments…
          </div>
        ) : assignments.length === 0 && !me ? (
          <div
            style={{
              padding: 22,
              textAlign: 'center',
              color: 'var(--muted)',
              fontSize: 13,
            }}
          >
            You don't have a MineTwin workforce record yet. A coordinator
            needs to add you before assignments can be issued.
          </div>
        ) : assignments.length === 0 ? (
          <div
            style={{
              padding: 22,
              textAlign: 'center',
              color: 'var(--muted)',
              fontSize: 13,
            }}
          >
            {currentSite && allAssignments.length > 0
              ? `No assignments on ${currentSite.name}. Switch sites in the topbar if you have assignments elsewhere.`
              : 'No assignments yet. Your coordinator will assign an orientation when one is ready for you.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {assignments.map((a) => (
              <AssignmentCard
                key={a.assignmentId.toString()}
                assignment={a}
                groupNote={groupNoteBySession.get(a.sessionId.toString()) ?? null}
                orientationName={
                  orientationById.get(a.orientationId)?.displayName ?? a.orientationId
                }
                steps={stepsByOrientation.get(a.orientationId) ?? []}
                busy={busyAssignmentId === a.assignmentId}
                disabled={busyAssignmentId !== null && busyAssignmentId !== a.assignmentId}
                launchEnabled={launcher.launchEnabled}
                launchStatusMessage={launcher.statusMessage}
                launchChannel={channelLabel(channelConfigs, launcher.effectiveAppId, channel)}
                launchVersionLabel={launcher.versionLabel}
                installState={launcher.installState}
                downloadActive={launcher.downloadActive}
                download={launcher.download}
                onLaunch={() => void handleLaunch(a)}
                onUnassign={canUnassign ? () => handleUnassign(a) : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function AssignmentCard({
  assignment: a,
  groupNote,
  orientationName,
  steps,
  busy,
  disabled,
  launchEnabled,
  launchStatusMessage,
  launchChannel,
  launchVersionLabel,
  installState,
  downloadActive,
  download,
  onLaunch,
  onUnassign,
}: {
  assignment: MyAssignmentRow;
  /** Group-run status line when this assignment runs in a shared session
   *  (group plan Phase 5): lobby wait text / running / paused-by. */
  groupNote: string | null;
  orientationName: string;
  steps: ReturnType<typeof useOrientationStepOrder>['stepsByOrientation'] extends Map<string, infer V> ? V : never;
  busy: boolean;
  disabled: boolean;
  /** Local launcher EXE is online + MineZero app row resolved — Launch
   *  button enable signal from `useLauncherReadiness`. */
  launchEnabled: boolean;
  /** Human-readable launcher status, shown as a tooltip on the
   *  disabled Launch button so trainees see why it's greyed. */
  launchStatusMessage: string;
  /** Effective launcher channel (shared selection / env override) — shown in
   *  the Launch button tooltip so the trainee knows which build will run. */
  launchChannel: string;
  /** Resolved version label for the channel, or `undefined` when none resolved
   *  (launcher will use its installed default). */
  launchVersionLabel: string | undefined;
  /** #193: Download / Update / Launch before the click. */
  installState: InstallState;
  /** #194: a download for this app is already running — don't queue another. */
  downloadActive: boolean;
  /** Live download progress for the resolved version (the launcher downloads it
   *  on first Launch); `null` when nothing is downloading. */
  download: LauncherDownload | null;
  onLaunch: () => void;
  /** Admin-only; `undefined` hides the Unassign button entirely. */
  onUnassign?: () => void;
}) {
  const statusTag = a.status.tag;
  const currentIndex = a.currentStepId
    ? steps.findIndex((s) => s.stepId === a.currentStepId)
    : -1;
  const currentStep = currentIndex >= 0 ? steps[currentIndex] : null;
  const totalSteps = steps.length;
  const progressLabel =
    totalSteps > 0 && currentIndex >= 0
      ? `Step ${currentIndex + 1} of ${totalSteps}`
      : totalSteps > 0
        ? `${totalSteps} step${totalSteps === 1 ? '' : 's'}`
        : '';

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 14,
        display: 'grid',
        gap: 10,
        background: 'var(--panel-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{orientationName}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
            <code>{a.assignmentId.toString()}</code> · {a.projectId}
            {/* Single-length mode: only a legacy timed assignment has a length. */}
            {formatLegacyVersionTag(a.orientationVersion.tag)
              ? ` · length ${formatLegacyVersionTag(a.orientationVersion.tag)}`
              : ''}
            {progressLabel ? ` · ${progressLabel}` : ''}
          </div>
          {groupNote ? (
            <div style={{ fontSize: 11.5, color: 'var(--amber, var(--good))', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon of={PortalIcons.view.cohorts} size={13} /> {groupNote}
            </div>
          ) : null}
        </div>
        <StatusPill status={statusTag} />
      </div>

      {currentStep ? (
        <div
          style={{
            padding: '8px 10px',
            background: 'var(--panel-3)',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          <div style={{ color: 'var(--muted)', fontSize: 11 }}>Current step</div>
          <div style={{ fontWeight: 600 }}>{currentStep.displayName}</div>
          <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 2 }}>
            {currentStep.durationMinutes} min
          </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {(statusTag === 'NotStarted' || statusTag === 'InProgress') && (
          <LaunchButton
            sessionId={a.sessionId}
            launchEnabled={launchEnabled}
            launchStatusMessage={launchStatusMessage}
            channel={launchChannel}
            versionLabel={launchVersionLabel}
            installState={installState}
            downloadActive={downloadActive}
            busy={busy}
            disabled={disabled}
            onLaunch={onLaunch}
          />
        )}
        {(statusTag === 'NotStarted' || statusTag === 'InProgress') && (
          <DownloadStatus download={download} />
        )}
        {/* Step progression is driven by Unreal's `report_step_beat`
            once the orientation is launched — the portal no longer
            exposes manual Advance / Mark complete buttons. */}
        {statusTag === 'Completed' && (
          <span style={{ fontSize: 12, color: 'var(--good)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon of={Icons.status.success} size={12} /> Completed
            {a.completedAt
              ? ` ${new Date(a.completedAt).toLocaleDateString()}`
              : ''}
          </span>
        )}
        {statusTag === 'Cancelled' && (
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Cancelled</span>
        )}
        {onUnassign && (
          <button
            type="button"
            className="btn danger sm"
            style={{ marginLeft: 'auto' }}
            disabled={busy || disabled}
            onClick={onUnassign}
            title="Remove this assignment from your list (admin)"
          >
            Unassign
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Per-row Launch button. Disabled paths:
 *  - sessionId is still `0n` — the onboarding session hasn't been
 *    provisioned yet (the `execute_assign_orientation` scheduled
 *    procedure writes it asynchronously). Tooltip nudges the trainee
 *    to try again rather than letting them spawn the client with
 *    `-MpSessionId=0` which would land them in the default session.
 *  - launcher EXE not running — the queueLaunch reducer fallback used
 *    by the topbar widget drops `launch_args` silently, so we never
 *    fall back here; we just disable the button with the launcher's
 *    own status message as the tooltip ("Launcher not running" /
 *    "Not connected to SpacetimeDB").
 */
function LaunchButton({
  sessionId,
  launchEnabled,
  launchStatusMessage,
  channel,
  versionLabel,
  installState,
  downloadActive,
  busy,
  disabled,
  onLaunch,
}: {
  sessionId: bigint;
  launchEnabled: boolean;
  launchStatusMessage: string;
  channel: string;
  versionLabel: string | undefined;
  installState: InstallState;
  downloadActive: boolean;
  busy: boolean;
  disabled: boolean;
  onLaunch: () => void;
}) {
  const noSession = sessionId === 0n;
  // #194: a second click while the build is coming down would queue another
  // launch for the same version — the banner already says what is happening.
  // busy / disabled = a call is in flight (this card or another one); the
  // rest are reasons the trainee should read on hover (#262).
  const isBlocked = noSession || !launchEnabled || downloadActive;
  // When ready, the tooltip names the channel (and resolved version) so the
  // trainee knows which build this launches — it mirrors the launcher widget's
  // current selection (or a VITE_MINEZERO_CHANNEL override).
  const readyTitle = versionLabel
    ? `Launch v${versionLabel} on the ${channel} channel`
    : `Launch on the ${channel} channel (launcher will use its installed default)`;
  const title = noSession
    ? 'Onboarding session is still being provisioned. Try again in a moment.'
    : !launchEnabled
      ? launchStatusMessage
      : downloadActive
        ? 'The launcher is downloading this build — it starts on its own when the download finishes.'
        : installState === 'download'
          ? 'Not on this machine yet — this downloads the build, then launches it.'
          : installState === 'update'
            ? `A newer build is pinned${versionLabel ? ` (v${versionLabel})` : ''} — this downloads it, then launches.`
            : readyTitle;
  return (
    <GatedButton
      className="btn sm"
      busy={busy || disabled}
      reason={isBlocked ? title : null}
      hint={title}
      onClick={onLaunch}
    >
      {busy
        ? 'Launching…'
        : downloadActive
          ? 'Downloading…'
          : installState === 'download'
            ? 'Download'
            : installState === 'update'
              ? 'Update'
              : 'Launch'}
    </GatedButton>
  );
}

/**
 * Inline download feedback shown next to the Launch button. The launcher
 * downloads the resolved version on first Launch (HTTP 202 "downloading"); this
 * reflects `my_download_progress` so the trainee sees progress and any failure
 * instead of a button that looks like it did nothing.
 */
function DownloadStatus({ download }: { download: LauncherDownload | null }) {
  if (!download) return null;
  // Completed → the app launched (or is about to); nothing to show.
  if (download.status === 'Completed') return null;
  if (download.status === 'Failed') {
    return (
      <span style={{ fontSize: 11.5, color: 'var(--bad)' }}>
        Download failed{download.errorMessage ? `: ${download.errorMessage}` : ''}
      </span>
    );
  }
  const pct = download.percent;
  return (
    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
      Downloading{pct !== null ? ` ${pct}%` : '…'}
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    NotStarted: {
      bg: 'var(--panel-3)',
      fg: 'var(--muted)',
      label: 'Not started',
    },
    InProgress: {
      bg: 'var(--warn-soft)',
      fg: 'var(--warn)',
      label: 'In progress',
    },
    Completed: {
      bg: 'var(--good-soft)',
      fg: 'var(--good)',
      label: 'Completed',
    },
    Cancelled: {
      bg: 'var(--panel-3)',
      fg: 'var(--muted)',
      label: 'Cancelled',
    },
  };
  const s = styles[status] ?? styles.NotStarted;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 10,
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 600,
        height: 'fit-content',
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}
